import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Button,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Switch,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAppContext } from "AppContext";
import CommonPagination from "components/CommonPagination";
import GKTable from "components/GKTable";
import useToast from "hooks/useToast";
import { useState } from "react";
import { CgMathPlus } from "react-icons/cg";
import { useNavigate } from "react-router-dom";
import accessBoardService from "servies/accessBoard";
import axiosInstance from "utils/axiosInstance";
import CreateAdvisorModal from "../CreateAdvisorModal";
import AddTrialPeriod from "./AddTrialPeriod";
import EditPlan from "./EditPlan";

const Advisor = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [openModal, setOpenModal] = useState(false);
  const [advisorCreateModal, setAdvisorCreateModal] = useState(false);
  const [openTrialModal, setOpenTrialModal] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);

  const {
    state: { commonFilter },
  } = useAppContext();

  const [anchorEl, setAnchorEl] = useState(null);

  const requestBody = {
    take: commonFilter?.advisorList?.take,
    skip: commonFilter?.advisorList?.skip,
    sort: JSON.stringify(
      commonFilter?.advisorList?.sortModel?.map((data) => {
        return {
          selector: data.field,
          desc: data.sort !== "asc",
        };
      })
    ),
    search: commonFilter?.advisorList?.search || "",
  };

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["ADVISOR_LIST", requestBody],
    queryFn: () =>
      axiosInstance.get(`/super_admin/advisors/`, { params: requestBody }),
  });

  const { mutate: deleteClient } = useMutation({
    mutationKey: ["DELETE_CLIENT"],
    mutationFn: (data) =>
      axiosInstance.delete(`/super_admin/${data}/delete-advisor/`),
    onSuccess: () => {
      refetch();
      toast.show("success", "Advisor deleted successfully");
    },
    onError: () => {
      toast.show("error", "Failed to delete advisor");
    },
  });

  const cusPagination = () => {
    return (
      <CommonPagination
        name="advisorList"
        totalCount={data?.data?.totalCount || 0}
      />
    );
  };

  const handleMenuOpen = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const columns = [
    {
      headerName: "User Details",
      field: "companyName",
      disableColumnMenu: true,
      flex: 1,
      minWidth: 150,
    },
    {
      headerName: "Mobile No.",
      field: "phone",
      disableColumnMenu: true,
      flex: 1,
      minWidth: 100,
      valueGetter: (row) => row?.row?.phone || "-",
    },
    {
      headerName: "Email",
      field: "email",
      disableColumnMenu: true,
      flex: 1,
      minWidth: 100,
      valueGetter: (row) => row?.row?.email || "-",
    },
    {
      headerName: "Portfolio Count",
      field: "portfolios",
      disableColumnMenu: true,
      flex: 1,
      minWidth: 90,
      valueGetter: (row) => row?.row?.portfolios || "-",
    },
    {
      headerName: "Register Date",
      field: "createdOn",
      disableColumnMenu: true,
      flex: 1,
      minWidth: 120,
      valueGetter: (row) => row?.row?.createdOn || 0,
    },
    {
      headerName: "Trial Expiry Date",
      field: "clients",
      disableColumnMenu: true,
      flex: 1,
      minWidth: 80,
      valueGetter: (row) => row?.row?.clients || 0,
    },
    {
      headerName: "Payment Date",
      field: "paymentDate",
      disableColumnMenu: true,
      flex: 1,
      minWidth: 80,
    },
    {
      headerName: "Last Used",
      field: "lastDate",
      disableColumnMenu: true,
      flex: 1,
      minWidth: 80,
    },
    {
      headerName: "Plan status",
      field: "plan",
      disableColumnMenu: true,
      flex: 1,
      minWidth: 100,
    },
    {
      headerName: "Account Status",
      field: "status",
      disableColumnMenu: true,
      flex: 1,
      minWidth: 100,
    },
    {
      headerName: "Grant Access",
      flex: 1,
      field: "grantAccess",
      align: "right",
      headerAlign: "right",
      renderCell: (item) => (
        <Switch
          checked={item?.row?.isActive && item?.row?.isVerified}
          onClick={(event) => {
            event.stopPropagation();
          }}
          onChange={async (value) => {
            const response = await accessBoardService.grantAccess({
              userPhoneNumber: item?.row?.phone,
              hasAccess: value.target.checked,
            });
            if (response.status !== 200) {
              toast.show("error", "Failed to grant access");
            }
            toast.show(
              "success",
              `Access ${value ? "granted" : "revoked"} successfully`
            );
          }}
        />
      ),
    },
    {
      headerName: "Action",
      field: "action",
      disableColumnMenu: true,
      flex: 1,
      minWidth: 90,
      renderCell: (params) => (
        <div>
          <IconButton
            aria-label="more"
            aria-controls="long-menu"
            aria-haspopup="true"
            onClick={(event) => {
              event.stopPropagation();
              handleMenuOpen(event, params.id);
            }}
          >
            <MoreVertIcon />
          </IconButton>
          {selectedRowId === params.id && (
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                style: {
                  maxHeight: 200,
                },
              }}
            >
              <MenuItem onClick={() => setOpenModal(true)}>
                Edit Plan Status
              </MenuItem>
              <MenuItem onClick={() => setOpenTrialModal(true)}>
                Add Trial Period
              </MenuItem>
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/app/preference/${params.row.phone}`);
                }}
              >
                Wallet Top Up
              </MenuItem>
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  deleteClient(params.row.id);
                  // handleMenuClose();
                }}
              >
                Delete
              </MenuItem>
              <MenuItem onClick={handleMenuClose}>Freeze Account</MenuItem>
            </Menu>
          )}
        </div>
      ),
    },
  ];

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <GKTable
          loading={isFetching}
          columns={columns}
          rows={data?.data?.data || []}
          maxHeight={"calc(100vh - 306px)"}
          searchPlaceholder="Search for Advisor"
          CustomPagination={!isFetching && cusPagination}
          onRowClick={(row) => navigate(`/app/advisor/${row.id}/details`)}
          filterButton={false}
          name="advisorList"
          checkboxSelection
          headerComponent={
            <Button
              sx={{
                fontSize: "0.7rem",
                fontFamily: '"Arial", sans-serif',
                textTransform: "none",
              }}
              onClick={() => setAdvisorCreateModal(true)}
              variant="contained"
              startIcon={<CgMathPlus size={14} />}
            >
              Create Advisor
            </Button>
          }
          tableName="advisor_list"
        />
      </Grid>
      {openModal && <EditPlan open={openModal} setOpen={setOpenModal} />}
      {openTrialModal && (
        <AddTrialPeriod open={openTrialModal} setOpen={setOpenTrialModal} />
      )}
      {advisorCreateModal && (
        <CreateAdvisorModal
          open={advisorCreateModal}
          setOpen={setAdvisorCreateModal}
        />
      )}
    </Grid>
  );
};

export default Advisor;
